import type { Notification } from '@/types';
import { Avatar } from '@/components/ui';
import {
  formatTimestamp,
  getNotificationIcon,
  getNotificationIconVariant,
  isAvatarType,
} from '@/services/notification.helpers';

export interface NotificationItemProps {
  notification: Notification;
  onTap: (notification: Notification) => void;
}

const VARIANT_STYLES: Record<string, string> = {
  default: 'bg-muted text-muted-foreground',
  error: 'bg-danger-light text-danger',
  success: 'bg-success-light text-success',
};

export function NotificationItem({
  notification,
  onTap,
}: NotificationItemProps) {
  const isUnread = !notification.read_at;
  const Icon = getNotificationIcon(notification.type);
  const variant = getNotificationIconVariant(notification.type);
  const showAvatar = isAvatarType(notification.type);

  return (
    <div
      className={[
        'flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors',
        isUnread ? 'bg-muted' : 'bg-card',
      ].join(' ')}
      onClick={() => onTap(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTap(notification);
        }
      }}
    >
      {/* Icon / Avatar slot */}
      <div className="pt-0.5 shrink-0">
        {showAvatar ? (
          <Avatar
            size="sm"
            initials={notification.title.charAt(0).toUpperCase()}
          />
        ) : (
          <div
            className={[
              'w-8 h-8 rounded-full flex items-center justify-center',
              VARIANT_STYLES[variant] ?? VARIANT_STYLES.default,
            ].join(' ')}
          >
            <Icon size={16} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={[
            'text-sm',
            isUnread
              ? 'font-semibold text-foreground'
              : 'font-normal text-foreground',
          ].join(' ')}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {notification.body}
          </p>
        )}
      </div>

      {/* Timestamp. No X (YAR-496 behaviour 3): deleting a row can't be
          undone and erases the only record that a deleted task or event
          existed; rows leave the feed only after the 30-day window. */}
      <div className="flex items-start gap-1.5 shrink-0 pt-0.5">
        <span className="text-xs text-muted-foreground">
          {formatTimestamp(notification.created_at)}
        </span>
      </div>
    </div>
  );
}
